<template>
  <div>
    <v-container fluid>
      <v-expansion-panels v-model="filterPanel" class="mb-6" accordion>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center justify-space-between" style="width: 100%;">
              <div class="d-flex align-center">
                <v-icon left color="primary">mdi-filter</v-icon>
                <span class="primary--text">Filters</span>
                <v-chip
                  v-if="activeFiltersCount > 0"
                  small
                  color="primary"
                  text-color="white"
                  class="ml-2"
                >
                  {{ activeFiltersCount }} {{ activeFiltersCount === 1 ? 'filter' : 'filters' }} applied
                </v-chip>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="reportType"
                label="Report Type"
                placeholder="Select Report Type"
                :items="reportTypes"
                item-text="label"
                item-value="value"
                outlined
                dense
                prepend-inner-icon="mdi-chart-bar"
                @change="onReportTypeChange"
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.vessel_id"
                label="Vessel"
                placeholder="All Vessels"
                :items="vessels"
                item-text="name"
                item-value="id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-ferry"
                @change="onFilterChange"
                hide-details
              >
                <template v-slot:prepend-item>
                  <v-text-field
                    v-model="vesselSearchQuery"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search vessels..."
                    clearable
                    class="px-4"
                    @input="filterVessels"
                  ></v-text-field>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.customer_id"
                label="Customer"
                placeholder="All Customers"
                :items="filteredCustomers"
                item-text="name"
                item-value="id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-account"
                @change="onFilterChange"
                hide-details
              >
                <template v-slot:prepend-item>
                  <v-text-field
                    v-model="customerSearchQuery"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search customers..."
                    clearable
                    class="px-4"
                    @input="filterCustomers"
                  ></v-text-field>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3" v-if="reportType !== 'top_vessels_by_customer'">
              <v-select
                v-model="filters.service_id"
                label="Service"
                placeholder="All Services"
                :items="filteredServices"
                item-text="name"
                item-value="id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-cog"
                @change="onFilterChange"
                hide-details
              >
                <template v-slot:prepend-item>
                  <v-text-field
                    v-model="serviceSearchQuery"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search services..."
                    clearable
                    class="px-4"
                    @input="filterServices"
                  ></v-text-field>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3" v-if="reportType !== 'top_vessels_by_customer'">
              <v-select
                v-model="filters.supplier_id"
                label="Supplier"
                placeholder="All Suppliers"
                :items="filteredSuppliers"
                item-text="name"
                item-value="id"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-truck-delivery"
                @change="onFilterChange"
                hide-details
              >
                <template v-slot:prepend-item>
                  <v-text-field
                    v-model="supplierSearchQuery"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search suppliers..."
                    clearable
                    class="px-4"
                    @input="filterSuppliers"
                  ></v-text-field>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6" md="3" v-if="reportType === 'top_vessels_by_customer'">
              <v-select
                v-model="filters.job_scope"
                label="Job Scope"
                placeholder="All Job Scopes"
                :items="jobScopes"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-script"
                @change="onFilterChange"
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="Date Range"
                    placeholder="Select Date Range"
                    prepend-inner-icon="mdi-calendar-range"
                    :append-icon="dateRangeText ? 'mdi-close' : ''"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                    @click:append="clearDateRange()"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateRange"
                  range
                  scrollable
                  @input="onDateRangeChange"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dateMenu = false">Close</v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <top-services-report
        v-if="reportType === 'top_services'"
        :filters="getFilterParams()"
        :key="filterKey"
        @filter-change="onServiceReportFilterChange"
      ></top-services-report>

      <top-vessels-by-customer-report
        v-if="reportType === 'top_vessels_by_customer'"
        :filters="getFilterParams()"
        :key="filterKey"
        @filter-change="onServiceReportFilterChange"
      ></top-vessels-by-customer-report>

      <v-card v-if="!reportType || (reportType !== 'top_services' && reportType !== 'top_vessels_by_customer')" elevation="2">
        <v-card-text>
          <div class="text-center py-8">
            <v-icon size="64" color="grey lighten-1">mdi-chart-line</v-icon>
            <div class="mt-4 text-h6 grey--text">Drill Down Reports</div>
            <div class="text-caption grey--text">Select a report type above to generate reports</div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment';
import TopServicesReport from './blocks/TopServicesReport.vue';
import TopVesselsByCustomerReport from './blocks/TopVesselsByCustomerReport.vue';
import { ensureSelectedVesselInList } from '../../utilities/vesselHelpers';

export default {
  name: 'DrillDownReports',
  components: {
    TopServicesReport,
    TopVesselsByCustomerReport
  },
  data() {
      return {
        filterPanel: [],
        reportType: 'top_vessels_by_customer',
        reportTypes: [
          { label: 'Top Services', value: 'top_services' },
          { label: 'Top Vessels by Customer', value: 'top_vessels_by_customer' }
        ],
        filterKey: 0,
        filters: {
          vessel_id: null,
          customer_id: null,
          service_id: null,
          supplier_id: null,
          job_scope: null,
          start_date: null,
          end_date: null
        },
        jobScopes: ["Full Agent", "Owner's Matter", "Charterer", "Cargo Operation", "Handover / takeover", "OPL(outside port limits)", "Shipyard / Dry Dock", "Sheriff Agent", "Crew Service", "Miscellaneous"],
        vessels: [],
        customers: [],
        services: [],
        suppliers: [],
        vesselSearchQuery: '',
        customerSearchQuery: '',
        serviceSearchQuery: '',
        supplierSearchQuery: '',
        filteredCustomers: [],
        filteredServices: [],
        filteredSuppliers: [],
        dateMenu: false,
        dateRange: null,
        loading: {
          vessels: false,
          customers: false,
          services: false,
          suppliers: false
        }
      };
  },
  computed: {
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2) return '';
      const fmt = (d) => moment(d, 'YYYY-MM-DD').format('DD-MM-YYYY');
      return `${fmt(this.dateRange[0])} ~ ${fmt(this.dateRange[1])}`;
    },
    hasActiveFilters() {
      return !!(
        this.filters.vessel_id ||
        this.filters.customer_id ||
        this.filters.service_id ||
        this.filters.supplier_id ||
        (this.dateRange && this.dateRange.length === 2)
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.reportType) count++;
      if (this.filters.vessel_id) count++;
      if (this.filters.customer_id) count++;
      if (this.reportType !== 'top_vessels_by_customer' && this.filters.service_id) count++;
      if (this.reportType !== 'top_vessels_by_customer' && this.filters.supplier_id) count++;
      if (this.reportType === 'top_vessels_by_customer' && this.filters.job_scope) count++;
      if (this.dateRange && this.dateRange.length === 2) count++;
      return count;
    }
  },
  methods: {
    async loadDropdowns() {
      await Promise.all([
        this.loadVessels(),
        this.loadCustomers(),
        this.loadServices(),
        this.loadSuppliers()
      ]);
    },
    
    async loadVessels() {
      this.loading.vessels = true;
      try {
        const response = await this.$api.vessels.dropdown({ page: 1, per_page: 20 });
        this.vessels = response.vessels || [];
        this.vessels = await ensureSelectedVesselInList({
          vesselId: this.filters.vessel_id,
          vesselList: this.vessels,
          existingVessels: this.vessels,
          loadVesselFn: (id) => this.ensureVesselLoaded(id)
        });
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load vessels: ${error.message}`,
        });
      } finally {
        this.loading.vessels = false;
      }
    },
    
    async loadCustomers() {
      this.loading.customers = true;
      try {
        const response = await this.$api.cards.index('customer');
        this.customers = (response.cards || []).filter(card => card.is_active);
        this.filteredCustomers = this.customers;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load customers: ${error.message}`,
        });
      } finally {
        this.loading.customers = false;
      }
    },
    
    async loadServices() {
      this.loading.services = true;
      try {
        const response = await this.$api.service_catalogs.index();
        this.services = (response.service_catalogs || []).filter(service => service.is_active);
        this.filteredServices = this.services;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load services: ${error.message}`,
        });
      } finally {
        this.loading.services = false;
      }
    },
    
    async loadSuppliers() {
      this.loading.suppliers = true;
      try {
        const response = await this.$api.cards.index('supplier');
        this.suppliers = (response.cards || []).filter(card => card.is_active);
        this.filteredSuppliers = this.suppliers;
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to load suppliers: ${error.message}`,
        });
      } finally {
        this.loading.suppliers = false;
      }
    },
    
    async filterVessels() {
      if (!this.vesselSearchQuery || !this.vesselSearchQuery.trim()) {
        await this.loadVessels();
        return;
      }
      
      try {
        this.loading.vessels = true;
        const response = await this.$api.vessels.dropdown({ 
          page: 1, 
          per_page: 20,
          search: this.vesselSearchQuery.trim()
        });
        this.vessels = response.vessels || [];
        this.vessels = await ensureSelectedVesselInList({
          vesselId: this.filters.vessel_id,
          vesselList: this.vessels,
          existingVessels: this.vessels,
          loadVesselFn: (id) => this.ensureVesselLoaded(id)
        });
      } catch (error) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Failed to search vessels: ${error.message}`,
        });
      } finally {
        this.loading.vessels = false;
      }
    },
    
    filterCustomers() {
      if (!this.customerSearchQuery) {
        this.filteredCustomers = this.customers;
      } else {
        this.filteredCustomers = this.customers.filter(customer =>
          customer.name.toLowerCase().includes(this.customerSearchQuery.toLowerCase())
        );
      }
    },
    
    filterServices() {
      if (!this.serviceSearchQuery) {
        this.filteredServices = this.services;
      } else {
        this.filteredServices = this.services.filter(service =>
          service.name.toLowerCase().includes(this.serviceSearchQuery.toLowerCase())
        );
      }
    },
    
    filterSuppliers() {
      if (!this.supplierSearchQuery) {
        this.filteredSuppliers = this.suppliers;
      } else {
        this.filteredSuppliers = this.suppliers.filter(supplier =>
          supplier.name.toLowerCase().includes(this.supplierSearchQuery.toLowerCase())
        );
      }
    },
    
    onDateRangeChange() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.filters.start_date = this.dateRange[0];
        this.filters.end_date = this.dateRange[1];
        this.dateMenu = false;
        this.onFilterChange();
      } else if (!this.dateRange || this.dateRange.length === 0) {
        this.dateRangeText = '';
      }
    },
    
    clearDateRange() {
      this.dateRange = null;
      this.dateRangeText = '';
      this.filters.start_date = null;
      this.filters.end_date = null;
      this.updateURLFromFilters();
      this.onFilterChange();
    },
    
    onFilterChange() {
      this.updateURLFromFilters();
    },
    
    onReportTypeChange() {
      if (this.reportType !== 'top_services') {
        this.filters.service_id = null;
        this.filters.supplier_id = null;
      }
      if (this.reportType !== 'top_vessels_by_customer') {
        this.filters.job_scope = null;
      }
      this.filterKey++;
      this.updateURLFromFilters();
    },
    
    async onServiceReportFilterChange(serviceId, additionalFilters) {
      if (serviceId) {
        this.filters.service_id = serviceId;
      }
      if (additionalFilters) {
        Object.assign(this.filters, additionalFilters);
        
        if (additionalFilters.vessel_id) {
          await this.ensureVesselLoaded(additionalFilters.vessel_id);
        }
      }
      this.filterKey++;
      this.updateURLFromFilters();
    },
    async ensureVesselLoaded(vesselId) {
      const vesselExists = this.vessels.some(v => v.id === vesselId);
      if (vesselExists) {
        return;
      }
      
      try {
        this.loading.vessels = true;
        const response = await this.$api.vessels.show(vesselId);
        const vessel = response.vessel;
        
        if (vessel) {
          if (!this.vessels.some(v => v.id === vesselId)) {
            this.vessels.push(vessel);
            this.vessels.sort((a, b) => a.name.localeCompare(b.name));
          }
        }
      } catch (error) {
        console.error('Failed to load vessel:', error);
      } finally {
        this.loading.vessels = false;
      }
    },
    
    clearFilters() {
      this.filters = {
        vessel_id: null,
        customer_id: null,
        service_id: null,
        supplier_id: null,
        job_scope: null,
        start_date: null,
        end_date: null
      };
      this.dateRange = null;
      this.reportType = null;
      this.updateURLFromFilters();
      this.onFilterChange();
    },
    
    readFiltersFromURL() {
      const query = this.$route.query;
      
      if (query.report_type) {
        const validReportTypes = this.reportTypes.map(rt => rt.value);
        if (validReportTypes.includes(query.report_type)) {
          this.reportType = query.report_type;
        }
      }
      
      if (query.vessel_id) {
        const vesselId = parseInt(query.vessel_id);
        if (!isNaN(vesselId)) {
          this.filters.vessel_id = vesselId;
        }
      }
      
      if (query.customer_id) {
        const customerId = parseInt(query.customer_id);
        if (!isNaN(customerId)) {
          this.filters.customer_id = customerId;
        }
      }
      
      if (query.service_id) {
        const serviceId = parseInt(query.service_id);
        if (!isNaN(serviceId)) {
          this.filters.service_id = serviceId;
        }
      }
      
      if (query.supplier_id) {
        const supplierId = parseInt(query.supplier_id);
        if (!isNaN(supplierId)) {
          this.filters.supplier_id = supplierId;
        }
      }
      
      if (query.job_scope) {
        if (this.jobScopes.includes(query.job_scope)) {
          this.filters.job_scope = query.job_scope;
        }
      }
      
      if (query.start_date && query.end_date) {
        this.filters.start_date = query.start_date;
        this.filters.end_date = query.end_date;
        this.dateRange = [query.start_date, query.end_date];
      }
    },
    
    updateURLFromFilters() {
      const query = {};
      
      if (this.reportType) {
        query.report_type = this.reportType;
      }
      
      if (this.filters.vessel_id) {
        query.vessel_id = this.filters.vessel_id.toString();
      }
      if (this.filters.customer_id) {
        query.customer_id = this.filters.customer_id.toString();
      }
      if (this.filters.service_id) {
        query.service_id = this.filters.service_id.toString();
      }
      if (this.filters.supplier_id) {
        query.supplier_id = this.filters.supplier_id.toString();
      }
      if (this.filters.job_scope) {
        query.job_scope = this.filters.job_scope;
      }
      if (this.filters.start_date) {
        query.start_date = this.filters.start_date;
      }
      if (this.filters.end_date) {
        query.end_date = this.filters.end_date;
      }
      
      this.$router.replace({
        query: query
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
        }
      });
    },
    
    getFilterParams() {
      const params = {};
      if (this.filters.vessel_id) params.vessel_id = this.filters.vessel_id;
      if (this.filters.customer_id) params.customer_id = this.filters.customer_id;
      if (this.filters.service_id) params.service_id = this.filters.service_id;
      if (this.filters.supplier_id) params.supplier_id = this.filters.supplier_id;
      if (this.filters.job_scope) params.job_scope = this.filters.job_scope;
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      return params;
    }
  },
  async created() {
    if (!this.$root.hasAccess('')) {
      this.$store.dispatch('setAlert', {
        status: 'error',
        show: true,
        message: "Please contact admin to access this page",
      });
      this.$router.push({name: 'home'});
      return;
    }
    this.readFiltersFromURL();
    
    if (!this.filters.start_date || !this.filters.end_date) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 30);
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      this.filters.start_date = startDateStr;
      this.filters.end_date = endDateStr;
      this.dateRange = [startDateStr, endDateStr];
    }
    
    await this.loadDropdowns();
    
    if (this.filters.vessel_id) {
      await this.ensureVesselLoaded(this.filters.vessel_id);
    }
    
    this.$nextTick(() => {
      if (this.filters.vessel_id && !this.vessels.find(v => v.id === this.filters.vessel_id)) {
        this.filters.vessel_id = null;
      }
      
      if (this.filters.customer_id && !this.customers.find(c => c.id === this.filters.customer_id)) {
        this.filters.customer_id = null;
      }
      
      if (this.filters.service_id && !this.services.find(s => s.id === this.filters.service_id)) {
        this.filters.service_id = null;
      }
      
      if (this.filters.supplier_id && !this.suppliers.find(s => s.id === this.filters.supplier_id)) {
        this.filters.supplier_id = null;
      }
      
      this.updateURLFromFilters();
    });
  },
  
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (!oldQuery || Object.keys(oldQuery).length === 0) {
          return;
        }
        
        const currentQuery = {
          report_type: this.reportType || undefined,
          vessel_id: this.filters.vessel_id ? this.filters.vessel_id.toString() : undefined,
          customer_id: this.filters.customer_id ? this.filters.customer_id.toString() : undefined,
          service_id: this.filters.service_id ? this.filters.service_id.toString() : undefined,
          supplier_id: this.filters.supplier_id ? this.filters.supplier_id.toString() : undefined,
          job_scope: this.filters.job_scope || undefined,
          start_date: this.filters.start_date || undefined,
          end_date: this.filters.end_date || undefined
        };
        
        const cleanCurrent = JSON.stringify(Object.fromEntries(
          Object.entries(currentQuery).filter(([_, v]) => v !== undefined)
        ));
        const cleanNew = JSON.stringify(Object.fromEntries(
          Object.entries(newQuery).filter(([_, v]) => v !== undefined && v !== '')
        ));
        
        if (cleanCurrent !== cleanNew) {
          this.readFiltersFromURL();
          this.filterKey++;
        }
      },
      deep: true,
      immediate: false
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>